import { dayText, galText } from "./accounts";
import { accountName, sideWords } from "./fill";
import { bandText } from "./forecast";
import { memberName } from "./memberNames";
import type { PlanStop, PlanSummary, PlanTruck, PlanView, PlanWhy, RoutePlan } from "./api";
import { byTruck } from "./sort";

export const WHY_TEXT: Record<PlanWhy, string> = {
  request: "Pickup request",
  risk: "Overflow risk",
  follow_up: "Not emptied last visit",
  min_visit: "Minimum visit due",
  full: "Full",
  near: "On the way",
};

/** The hover help of each reason chip (rule V-7). */
export const WHY_HELP: Record<PlanWhy, string> = {
  request: "This account has an open pickup request.",
  risk: "The chance that this container is full within 3 workdays if no truck comes on this day.",
  follow_up: "The last visit did not empty this container. The truck goes again.",
  min_visit: "The minimum visit period of this account ends soon. The driver inspects the container, also when it is not full.",
  full: "This container is probably near the target fill on this day.",
  near: "The truck goes near this account, and the oil in the container is worth the stop.",
};
export const EMPTIED_HELP = "A pickup after the plan emptied this container. The expected gallons of the plan are old.";
export const BELOW_FLOAT_HELP = "The float of this container reports that the oil is below the float.";

/** Colours of the trucks on the map, in truck order. Each is readable with dark text. */
export const TRUCK_COLOURS = ["#ffc98a", "#8fd3ff", "#b6f09c", "#f5a3c7", "#d9c2ff", "#ffe680", "#9ff0e0", "#ffb3a3"];

export const truckColour = (index: number) => TRUCK_COLOURS[index % TRUCK_COLOURS.length];

export const POLL_MS = 5_000;

const LEFT_OUT: [string, string][] = [
  ["not_on_the_map", "not on the map"],
  ["no_estimate", "with no fill estimate"],
  ["out_of_season", "out of season"],
  ["no_time", "closed in the shift"],
  ["on_a_route", "on a route already"],
  ["not_active", "on hold or closed"],
];

const TRUCKS_LEFT_OUT: [string, string][] = [
  ["trucks_with_a_route", "with a route already"],
  ["trucks_no_depot_point", "with no depot point"],
  ["trucks_short_shift", "with a shift too short"],
];

/** "07:05" from minutes after midnight. */
export function minutesText(minutes: number | null): string {
  if (minutes === null) return "–";
  const whole = Math.max(0, Math.round(minutes));
  return `${String(Math.floor(whole / 60) % 24).padStart(2, "0")}:${String(whole % 60).padStart(2, "0")}`;
}

export function statusText(plan: RoutePlan): string {
  if (plan.status === "running") return "Planning… This takes about 2 minutes.";
  if (plan.status === "failed") return `The plan failed: ${plan.error || "no cause given"}`;
  if (plan.status === "accepted") return "Accepted. Change the routes below.";
  return "Proposed. Accept a truck or all trucks to make the routes.";
}

export function madeText(plan: RoutePlan, timeZone?: string): string {
  const at = new Date(plan.made_at).toLocaleString("en-US", { timeZone, month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  const who = plan.made_by ? memberName(plan.made_by) : "a former member";
  if (plan.status === "running") return plan.nightly ? `Nightly plan started on ${at}` : `Started by ${who} on ${at}`;
  return plan.nightly ? `Nightly plan of ${at}` : `Planned by ${who} on ${at}`;
}

/** The plan with its trucks in truck order (D-4), so the cards, the map, the timeline and Accept all share one order and one colour per truck. */
export function inTruckOrder<T extends Pick<PlanView, "plan">>(view: T): T {
  if (!view.plan) return view;
  return { ...view, plan: { ...view.plan, trucks: byTruck(view.plan.trucks, (truck) => truck.truck) } };
}

/** The text of Accept all, for example "Accept all (1 truck)". */
export function acceptAllText(count: number): string {
  return `Accept all (${count} ${count === 1 ? "truck" : "trucks"})`;
}

/** The note after Accept, for one truck or more. */
export function acceptedText(trucks: string[]): string {
  if (trucks.length === 1) return `Saved: truck ${trucks[0]} has its route. Change it below.`;
  return `Saved: ${trucks.length} trucks have their routes. Change them below.`;
}

/** The chip of a stop: the overflow risk when it is the reason (V-6); serious from the early-pull risk (D-15). */
export function whyChip(stop: Pick<PlanStop, "why" | "risk" | "emptied_on" | "float_now">, earlyRisk: number, minVisitDays?: number): { text: string; serious: boolean; help: string } | null {
  if (stop.emptied_on) return { text: "Emptied after the plan", serious: false, help: EMPTIED_HELP };
  if (stop.float_now && !stop.float_now.full) return { text: "Below the float now", serious: false, help: BELOW_FLOAT_HELP };
  if (!stop.why) return null;
  const help = WHY_HELP[stop.why];
  if (stop.why === "risk") {
    const risk = Math.max(stop.risk ?? 0, stop.float_now?.risk ?? 0);
    return { text: riskText(risk) ?? WHY_TEXT.risk, serious: risk >= earlyRisk, help };
  }
  if (stop.why === "min_visit" && minVisitDays) return { text: `${WHY_TEXT.min_visit} (${minVisitDays} days)`, serious: false, help };
  return { text: WHY_TEXT[stop.why], serious: false, help };
}

export function summaryText(summary: PlanSummary): string {
  const stops = summary.stops ?? 0;
  const trucks = summary.trucks_used ?? 0;
  const parts = [`${stops} ${stops === 1 ? "stop" : "stops"}`, `${trucks} ${trucks === 1 ? "truck" : "trucks"}`];
  parts.push(`${galText(summary.gallons ?? 0)} expected`, `${summary.drive_h ?? 0} h driving`);
  if (summary.tanks_at_risk_left) parts.push(`${summary.tanks_at_risk_left} at risk left for later`);
  const late = summary.min_visits_left ?? 0;
  if (late) parts.push(`${late} minimum ${late === 1 ? "visit" : "visits"} left for later`);
  return parts.join(" · ");
}

/** The planner mode of a plan in the area mode, and why the plan did not use it. */
export function modeText(summary: PlanSummary): string | null {
  if (summary.mode !== "areas") return null;
  const out = summary.home_trucks_out ?? 0;
  if (summary.mode_used !== "areas")
    return `Full auto for this plan: ${out} trucks with home areas are not in service. The area mode needs all but one of them.`;
  const away = summary.off_route_stops ?? 0;
  const preference = summary.truck_preference === "soft" ? "soft" : "hard";
  const text = `Area mode, ${preference} truck preference. ${away} ${away === 1 ? "stop is" : "stops are"} off route.`;
  const days = summary.template_days ?? 0;
  if (days <= 0) return text;
  const areas = (summary.templates_kept ?? 0) + (summary.templates_made ?? 0);
  const fresh = summary.template_new_accounts ?? 0;
  const added = fresh ? ` ${fresh} new ${fresh === 1 ? "account gets its" : "accounts get their"} first visit.` : "";
  return `${text} ${areas} ${areas === 1 ? "area keeps" : "areas keep"} a fixed stop order for ${days} days.${added}`;
}

/** Rule V-13: the route keeps the stop order of its areas, and the day that a new order starts. */
export const templateText = (truck: PlanTruck): string | null =>
  truck.template_until ? `Fixed stop order of its areas. A new order starts on ${dayText(truck.template_until)}.` : null;

/** The title of the off-route mark of a stop (rule V-10). */
export const offRouteTitle = (truck: string) => `Off route: the area of this account is the home area of another truck, not of truck ${truck}.`;

/** What the planner left out, for example "Not planned: 3 not on the map, 2 out of season." */
export function leftOutText(summary: PlanSummary): string | null {
  const counts = summary.counts ?? {};
  const accounts = LEFT_OUT.filter(([key]) => counts[key]).map(([key, text]) => `${counts[key]} ${text}`);
  const trucks = TRUCKS_LEFT_OUT.filter(([key]) => counts[key]).map(([key, text]) => `${counts[key]} ${counts[key] === 1 ? "truck" : "trucks"} ${text}`);
  const parts = [
    ...(counts.no_road_matrix ? ["The road times are not ready yet."] : []),
    ...(accounts.length ? [`Accounts not planned: ${accounts.join(", ")}.`] : []),
    ...(trucks.length ? [`Not used: ${trucks.join(", ")}.`] : []),
  ];
  return parts.length ? parts.join(" ") : null;
}

/** Rule S-8: a pickup after the plan makes its gallons and its risk old. */
export const emptiedText = (stop: Pick<PlanStop, "emptied_on">): string | null => (stop.emptied_on ? `Emptied ${dayText(stop.emptied_on)}, after the plan` : null);

/** The expected load of a stop and its hover: "–" after a pickup, at most the float share below a fresh float report (SO-2500). */
export function expectedLoad(stop: Pick<PlanStop, "expected_gal" | "emptied_on" | "float_now">): { text: string; title: string | null } {
  if (stop.emptied_on) return { text: "–", title: emptiedText(stop) };
  const below = stop.float_now && !stop.float_now.full ? stop.float_now : null;
  if (below && stop.expected_gal !== null && (below.max_gal === null || stop.expected_gal > below.max_gal)) {
    const title = `The float reports below ${below.pct}%`;
    return { text: below.max_gal === null ? "–" : `≤ ${galText(below.max_gal)}`, title };
  }
  return { text: stop.expected_gal === null ? "–" : galText(stop.expected_gal), title: null };
}

export function riskText(risk: number | null): string | null {
  if (risk === null || risk < 0.01) return null;
  return `${Math.round(risk * 100)}% overflow risk`;
}

/** Rule S-8: the fill now, as a pickup after the plan resets it; an older API gives only the fill at the plan. */
export const stopShare = (stop: Pick<PlanStop, "fill_p50" | "fill_now">): number | null => (stop.fill_now ? stop.fill_now.p50 : stop.fill_p50);

/** The estimated fill of a stop: the band now, else the band or the median of the plan. */
export function fillText(stop: Pick<PlanStop, "fill_p10" | "fill_p50" | "fill_p90" | "fill_now" | "float_now">): string {
  if (stop.float_now) return sideWords(stop.float_now, stopShare(stop));
  if (stop.fill_now) return bandText(stop.fill_now);
  if (stop.fill_p10 != null && stop.fill_p90 != null && stop.fill_p50 != null) return bandText({ p10: stop.fill_p10, p50: stop.fill_p50, p90: stop.fill_p90 });
  return stop.fill_p50 === null ? "–" : `About ${Math.round(Math.min(stop.fill_p50, 1) * 100)}% full`;
}

export function truckText(truck: PlanTruck, depot: string | null): string {
  const stops = truck.stops.filter((stop) => stop.kind === "stop").length;
  const parts = [`${stops} ${stops === 1 ? "stop" : "stops"}`];
  if (truck.gallons !== null) parts.push(`${galText(truck.gallons)} expected`);
  if (truck.capacity !== null) parts.push(`${galText(truck.capacity)} truck`);
  const dumps = truck.stops.filter((stop) => stop.kind === "dump").length;
  if (dumps) parts.push(`${dumps} ${dumps === 1 ? "dump" : "dumps"} on the way`);
  if (truck.start !== null && truck.end !== null) parts.push(`${minutesText(truck.start)}–${minutesText(truck.end)}`);
  if (depot) parts.push(depot);
  return parts.join(" · ");
}

/** The stop number of each stop on its truck; a dump has none. */
export function stopNumbers(stops: PlanStop[]): (number | null)[] {
  let count = 0;
  return stops.map((stop) => (stop.kind === "stop" ? ++count : null));
}

/** The driver of each truck: the choice on the page, else the driver of its last route. */
export function driversOf(trucks: PlanTruck[], chosen: Record<string, string>, drivers: { id: string }[]): Record<string, string> {
  const known = new Set(drivers.map((person) => person.id));
  const found: Record<string, string> = {};
  for (const truck of trucks) {
    const pick = chosen[truck.truck] ?? truck.default_driver_id ?? "";
    if (known.has(pick)) found[truck.truck] = pick;
  }
  return found;
}

/** The trucks that Accept all sends, or null when a truck that is not accepted has no driver. */
export function acceptAll(trucks: PlanTruck[], drivers: Record<string, string>): { truck: string; driver_id: string }[] | null {
  const open = trucks.filter((truck) => !truck.accepted);
  if (!open.length || open.some((truck) => !drivers[truck.truck])) return null;
  return open.map((truck) => ({ truck: truck.truck, driver_id: drivers[truck.truck] }));
}

/** The map path of a truck: the depot, its stops (a dump goes back to the depot), the depot. */
export function truckPath(truck: PlanTruck, depot: { lat: number; lng: number } | null): { lat: number; lng: number }[] {
  const path: { lat: number; lng: number }[] = [];
  if (depot) path.push(depot);
  for (const stop of truck.stops) {
    if (stop.kind === "dump") {
      if (depot) path.push(depot);
    } else if (stop.lat !== null && stop.lng !== null) {
      path.push({ lat: stop.lat, lng: stop.lng });
    }
  }
  if (depot) path.push(depot);
  return path;
}

export const MAP_TRUCK_PARAM = "map_truck";
export const MAP_OPEN_PARAM = "map";

/** The plan map is open unless the URL says `map=0` (rule U-23). */
export const mapOpenOf = (search: string) => new URLSearchParams(search).get(MAP_OPEN_PARAM) !== "0";

/** The search part of the URL with the map choice; open is the default and has no parameter. */
export function withMapOpen(search: string, open: boolean): string {
  const params = new URLSearchParams(search);
  if (open) params.delete(MAP_OPEN_PARAM);
  else params.set(MAP_OPEN_PARAM, "0");
  const text = params.toString();
  return text ? `?${text}` : "";
}

/** The truck that the map shows from the URL, or "" for all trucks and for a truck not in the plan. */
export function mapTruckOf(search: string, trucks: Pick<PlanTruck, "truck">[]): string {
  const wanted = new URLSearchParams(search).get(MAP_TRUCK_PARAM) ?? "";
  return trucks.some((truck) => truck.truck === wanted) ? wanted : "";
}

/** The search part of the URL with the map truck, or without it for all trucks. */
export function withMapTruck(search: string, truck: string): string {
  const params = new URLSearchParams(search);
  if (truck) params.set(MAP_TRUCK_PARAM, truck);
  else params.delete(MAP_TRUCK_PARAM);
  const text = params.toString();
  return text ? `?${text}` : "";
}

export function mapTruckOption(truck: PlanTruck): string {
  const stops = truck.stops.filter((stop) => stop.kind === "stop").length;
  return `Truck ${truck.truck} · ${stops} ${stops === 1 ? "stop" : "stops"}`;
}

/** The hover text of a stop pin: the stop number, the account name and the truck. */
export function stopPinTitle(truck: string, number: number | null, stop: Pick<PlanStop, "name" | "number">): string {
  return `Stop ${number ?? "–"} · ${accountName(stop.number, stop.name)} · Truck ${truck}`;
}

/** "08:00–11:30", or null when the service window is not known. */
export function windowText(stop: Pick<PlanStop, "window_start" | "window_end">): string | null {
  return stop.window_start && stop.window_end ? `${stop.window_start}–${stop.window_end}` : null;
}

/** The route that the map lifts: the hovered or focused one, else the one of the open stop box, else the picked truck. */
export function litTruck(hover: string | null, open: string | null, shown: string): string | null {
  return hover ?? open ?? (shown || null);
}

export interface RouteLook {
  outline: boolean;
  opacity: number;
  zIndex: number;
}

/** The lifted route has a dark outline and sits on top; the other routes fade a little. */
export function routeLook(lit: string | null, truck: string): RouteLook {
  if (lit === null) return { outline: false, opacity: 0.9, zIndex: 10 };
  return lit === truck ? { outline: true, opacity: 1, zIndex: 30 } : { outline: false, opacity: 0.4, zIndex: 10 };
}

/** A pin fades only when another route is lifted. */
export const pinFaded = (lit: string | null, truck: string) => lit !== null && lit !== truck;
